import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import type { DemoCoupon, DemoCouponStatus } from '@staff-app/shared-types';
import { CopyButton } from '@/components/CopyButton';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER } from '@/lib/data-table';

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleDateString();
}

const money = (n?: number | null) => (n == null ? '—' : `$${n.toFixed(2)}`);

/**
 * Status badge. Three states, three tones, and no invented fourth: the value is DERIVED
 * server-side from `used` + `expiresAt`, so the UI renders what it is told rather than
 * re-deciding it here and risking a different answer to the same question.
 */
function StatusBadge({ status }: { status: DemoCouponStatus }) {
  const variant =
    status === 'USED' ? 'secondary' : status === 'EXPIRED' ? 'outline' : 'default';
  return (
    <Badge variant={variant as never} data-testid={`demo-status-${status}`}>
      {status}
    </Badge>
  );
}

/**
 * The demo-coupon ledger.
 *
 * Every column here is somebody's question: what code, for which device and how many (the two the
 * requester asked for by name), who it went to, what it cost us, who issued it, whether it has
 * been redeemed, and when it stops working. `reason` is not truncated to a tooltip — on a path
 * with no approver it is the only account of why the code exists at all.
 */
export function DemoCouponTable({ coupons }: { coupons: DemoCoupon[] }) {
  if (!coupons.length) {
    return (
      <p className="p-6 text-sm text-muted-foreground" data-testid="demo-coupons-empty">
        {'No demo coupons yet.'}
      </p>
    );
  }

  return (
    <Table className={DATA_TABLE_CELLS} data-testid="demo-coupons-table">
      <TableHeader className={DATA_TABLE_HEADER}>
        <TableRow>
          <TableHead>{'Code'}</TableHead>
          <TableHead>{'Device'}</TableHead>
          <TableHead className="text-right">{'Qty'}</TableHead>
          <TableHead>{'Customer'}</TableHead>
          <TableHead className="text-right">{'Value'}</TableHead>
          <TableHead>{'Reason'}</TableHead>
          <TableHead>{'Issued by'}</TableHead>
          <TableHead>{'Issued'}</TableHead>
          <TableHead>{'Expires'}</TableHead>
          <TableHead>{'Status'}</TableHead>
          <TableHead>{'Redeemed'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {coupons.map((c) => (
          <TableRow key={c.code} data-testid={`demo-coupon-row-${c.code}`}>
            {/* Same copy affordance as the Amazon ledger (requester, 2026-09-24): copying the
                code is why a staffer found the row. The shared CopyButton, not a hand-rolled
                clipboard call — its docblock asks new callers to reuse it. */}
            <TableCell className="font-mono">
              <span className="inline-flex items-center gap-1">
                {c.code}
                <CopyButton
                  value={c.code}
                  label={`Copy ${c.code}`}
                  testId={`demo-copy-code-${c.code}`}
                  stopPropagation
                />
              </span>
            </TableCell>
            <TableCell data-testid="demo-cell-device">{c.deviceName}</TableCell>
            <TableCell className="text-right" data-testid="demo-cell-qty">{c.quantity}</TableCell>
            {/* Replaced the Ship-to column: demo devices are US only, so it could only ever read
                "US" — a column that cannot vary is a column nobody reads. The region is still
                stored, and still priced into Value. */}
            <TableCell data-testid="demo-cell-customer">{c.customerEmail || '—'}</TableCell>
            <TableCell className="text-right font-mono">{money(c.amount)}</TableCell>
            <TableCell className="max-w-[28rem] whitespace-normal">{c.reason}</TableCell>
            <TableCell>{c.createdBy}</TableCell>
            <TableCell>{fmtDate(c.createdAt)}</TableCell>
            <TableCell>{fmtDate(c.expiresAt)}</TableCell>
            <TableCell>
              <StatusBadge status={c.status} />
            </TableCell>
            {/* The date, not a tick: "when" is the question anyone asks next, and an empty cell
                already says "not yet" without spending a column on a dash. */}
            <TableCell>{c.used ? fmtDate(c.usedAt) : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
